import React, { useState } from 'react';
import { ChevronDown, HelpCircle } from 'lucide-react';

const faqs = [
  {
    question: "What is BuildPilot App and how can it help me?",
    answer: "BuildPilot App is a free building intelligence platform built to help Aussies through the building process. Whether you're a first-time home builder, renovating, or building an investment property, we have 25+ tools to help – from a smart dashboard and cost tracker to builder licence checks, house and land search, design inspiration, and one-to-one support. Download it, tell us about your build, and the entire app adapts to your project."
  },
  {
    question: "How much does BuildPilot App cost?",
    answer: "BuildPilot App offers two tiers. Free gives you access to 25+ core tools including Cost Tracker, Milestones, Checklists, House & Land Search, New Home Designs, Find a Tradie, Find a Supplier, Documents, Builder Licence Check, Interior Design Inspiration, Mood Board Creator, and more. Full Throttle Premium is $19.99/week or $79.99/month, and adds powerful AI-driven features like Find a Builder (intelligent matching), Quote Comparison (with hidden cost detection), Defects Scanner, Facade Visualiser, 3D Interior, Furniture Planner, Email Forwarding & Intelligence, Project Health monitoring, and the BuildPilot Intelligence Adviser."
  },
  {
    question: "Can BuildPilot App help me find a builder?",
    answer: "Absolutely. Our free tier lets you search and compare builders, check their licences, and verify insurance. With Full Throttle Premium, Find a Builder uses BuildPilot Intelligence to analyse your budget, style, location, and must-haves, then shortlists the top 2-3 builders perfect for YOUR project. Choosing the wrong builder is the #1 cause of building nightmares – we take the guesswork out of the most important decision you'll make."
  },
  {
    question: "How does BuildPilot App Intelligence work?",
    answer: "BuildPilot App Intelligence is like having a digital building advisor by your side 24/7. It integrates across your entire project – monitoring costs, flagging potential overcharging, analysing builder quotes for hidden costs, scanning photos for defects, summarising confusing builder emails, and providing an overall project health score. It's trained on Australian building practices, regulations, and common issues. Ask any question about your build and get instant, knowledgeable answers tailored to your specific project."
  },
  {
    question: "Is BuildPilot App available in my city?",
    answer: "BuildPilot App works Australia-wide. We've got specific features and communities for Adelaide, Melbourne, Sydney, Brisbane, Perth, and other cities. House and land packages, builder databases, and government resources are all filtered to your state and location."
  },
  {
    question: "How do I track my building costs with BuildPilot App?",
    answer: "Set your budget, log quotes and contracts, track progress payments, and record variations as they come up. You'll see exactly where your money's going with real-time budget monitoring. Full Throttle Premium adds intelligent price validation that flags potential overcharging so you never pay more than you should."
  },
  {
    question: "Can I use BuildPilot App for renovations, not just new builds?",
    answer: "Absolutely. When you first open BuildPilot, you select your build type – new home, renovation, knock-down rebuild, or granny flat – and the entire app transforms to match. Every tool, checklist, and milestone adjusts to your specific project type."
  },
  {
    question: "What premium features do I get with Full Throttle?",
    answer: "Full Throttle Premium includes everything in Free, plus: Find a Builder (AI matching), Quote Comparison (AI analysis of hidden costs), Defects Scanner, Facade Visualiser, 3D Interior, Furniture Planner, Email Forwarding & Intelligence, Project Health monitoring, BuildPilot Intelligence Adviser (24/7 expert), plus intelligent upgrades to Cost Tracker, Milestones, and Checklists, and priority one-to-one support."
  }
];

const FAQSection = () => {
  const [openIndex, setOpenIndex] = useState(null);

  const toggleFaq = (index) => {
    setOpenIndex(openIndex === index ? null : index);
  };

  // Schema markup for FAQ
  const schemaData = {
    "@context": "https://schema.org",
    "@type": "FAQPage",
    "mainEntity": faqs.map(faq => ({
      "@type": "Question",
      "name": faq.question,
      "acceptedAnswer": {
        "@type": "Answer",
        "text": faq.answer
      }
    }))
  };

  return (
    <section id="faq" className="py-16 sm:py-24 bg-neutral-50">
      {/* Add schema markup */}
      <script type="application/ld+json" dangerouslySetInnerHTML={{ __html: JSON.stringify(schemaData) }} />
      
      <div className="max-w-4xl mx-auto px-4 sm:px-6 lg:px-8">
        <div className="text-center mb-12">
          <span className="inline-flex items-center gap-2 px-4 py-2 bg-purple-100 text-purple-700 rounded-full text-sm font-medium mb-4">
            <HelpCircle className="w-4 h-4" />
            Got Questions?
          </span>
          <h2 className="font-serif text-3xl sm:text-4xl text-gray-900 mb-4">
            Frequently Asked <span className="gradient-text">Questions</span>
          </h2>
          <p className="text-lg text-gray-600 max-w-2xl mx-auto">
            Everything you need to know about BuildPilot App and building your home in Australia.
          </p>
        </div>

        <div className="space-y-4">
          {faqs.map((faq, index) => (
            <div 
              key={index}
              className="bg-white rounded-2xl border border-gray-100 overflow-hidden transition-all duration-300 hover:shadow-md"
            >
              <button
                onClick={() => toggleFaq(index)}
                className="w-full px-6 py-5 flex items-center justify-between text-left"
                aria-expanded={openIndex === index}
                data-testid={`faq-question-${index}`}
              >
                <span className="font-semibold text-gray-900 pr-4">{faq.question}</span>
                <ChevronDown 
                  className={`w-5 h-5 text-purple-600 flex-shrink-0 transition-transform duration-300 ${
                    openIndex === index ? 'rotate-180' : ''
                  }`}
                />
              </button>
              
              <div 
                className={`overflow-hidden transition-all duration-300 ${
                  openIndex === index ? 'max-h-96' : 'max-h-0'
                }`}
              >
                <div className="px-6 pb-5 text-gray-600 leading-relaxed">
                  {faq.answer}
                </div>
              </div>
            </div>
          ))}
        </div>

        {/* Additional help CTA */}
        <div className="mt-12 text-center">
          <p className="text-gray-600 mb-4">Still have questions?</p>
          <a
            href="mailto:home@buildpilot.com.au"
            className="inline-flex items-center gap-2 px-6 py-3 rounded-full font-semibold text-purple-700 bg-purple-100 hover:bg-purple-200 transition-colors"
            data-testid="faq-contact-btn"
          >
            Contact Us
          </a>
        </div>
      </div>
    </section>
  );
};

export default FAQSection;
